import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Badges are 18px tall, 11px, and squared off at 4px rather than pill-shaped:
 * a page with forty of them reads as a table when they are rectangles and as
 * confetti when they are pills. The tinted variants use the semantic token pair
 * (`--dy-*` and `--dy-*-bg`) so they carry the same meaning in both themes.
 */
const badgeVariants = cva(
  'inline-flex h-[18px] shrink-0 items-center gap-1 rounded-sm px-1.5 text-2xs font-medium whitespace-nowrap',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground',
        secondary: 'bg-raised text-fg-2',
        outline: 'border border-line text-fg-2',
        destructive: 'bg-bad-bg text-bad',
        success: 'bg-ok-bg text-ok',
        warning: 'bg-warn-bg text-warn',
        gate: 'bg-gate-bg text-gate',
        info: 'bg-info-bg text-info',
        plan: 'bg-plan-bg text-plan',
      },
    },
    defaultVariants: { variant: 'secondary' },
  },
);

export type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

function Badge({
  className,
  variant,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  return (
    <span data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
